import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import SaveXlsxButton from '@/shared/components/SaveXlsxButton';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt0 = (v: string | number) =>
  Math.round(Number(v)).toLocaleString('uk-UA');

const MONTHS_UK = [
  'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
];

const RATE_LABEL: Record<string, string> = {
  '20': 'операції за ставкою 20 %',
  '14': 'операції за ставкою 14 %',
  '7': 'операції за ставкою 7 %',
  '0': 'операції за ставкою 0 %',
  EXEMPT: 'операції, звільнені від оподаткування',
  OTHER: 'інші операції',
};

export default function VatDeclarationPrintPage() {
  const [params] = useSearchParams();
  const orgId = params.get('organization_id') ?? '';
  const year = parseInt(params.get('period_year') ?? '0');
  const month = parseInt(params.get('period_month') ?? '0');

  const orgQ = useQuery({
    queryKey: ['org-by-id', orgId],
    queryFn: () => orgs.list({ limit: 200 }).then(
      (p) => p.items.find((o) => o.id === orgId)),
    enabled: !!orgId,
  });

  const data = useQuery({
    queryKey: ['vat-summary-print', orgId, year, month],
    queryFn: () => reports.vatSummary({
      organization_id: orgId,
      period_year: year,
      period_month: month,
    }),
    enabled: !!orgId && year > 0 && month > 0,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const d = data.data;
  const org = orgQ.data;

  if (!orgId || !year || !month) {
    return (
      <div className="card">
        <p>Невалідні параметри. Очікуються: organization_id, period_year, period_month.</p>
      </div>
    );
  }

  if (!d || !org) {
    return <div className="card"><p>Завантаження…</p></div>;
  }

  return (
    <div className="print-page vat-print">
      <style>{`
        .vat-print {
          background: white;
          color: black;
          padding: 2rem;
          max-width: 920px;
          margin: 0 auto;
          font-family: 'Times New Roman', serif;
          font-size: 11pt;
          line-height: 1.4;
        }
        .vat-print h1 { font-size: 14pt; text-align: center; margin: 0 0 0.5rem; }
        .vat-print h2 { font-size: 12pt; margin: 1rem 0 0.5rem; }
        .vat-print .head { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
        .vat-print .head .label { color: #555; font-size: 9pt; }
        .vat-print table { width: 100%; border-collapse: collapse; font-size: 10pt; }
        .vat-print th, .vat-print td { border: 1px solid black; padding: 4px 8px; }
        .vat-print th { background: #f5f5f5; font-weight: 600; text-align: center; }
        .vat-print td.num { text-align: right; font-variant-numeric: tabular-nums; }
        .vat-print td.line { width: 90px; text-align: center; }
        .vat-print .summary { margin-top: 1.5rem; padding: 1rem; border: 2px solid black; }
        .vat-print .signs { margin-top: 2rem; display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; }
        .vat-print .sign { border-bottom: 1px solid black; padding-bottom: 4px; min-height: 24px; }
        .vat-print .sign-label { font-size: 9pt; color: #555; margin-top: 4px; }
        .vat-print .draft-warning {
          margin-top: 2rem; padding: 0.75rem; border: 1px solid #888;
          background: #fef9c3; font-size: 9pt;
        }
        .vat-print .actions { display: flex; gap: 0.5rem; margin-bottom: 1rem; }
        @media print {
          .vat-print .actions { display: none; }
          .vat-print { padding: 0; }
          body { background: white !important; }
        }
      `}</style>

      <div className="actions print-hide">
        <button onClick={() => window.print()} className="primary">друкувати</button>
        <button onClick={() => window.history.back()}>назад</button>
        <SaveXlsxButton />
      </div>

      <h1>Податкова декларація з податку на додану вартість</h1>
      <p style={{ textAlign: 'center', margin: 0 }}>
        за {MONTHS_UK[month - 1]} {year} року
      </p>

      <div className="head" style={{ marginTop: '1.5rem' }}>
        <div>
          <div className="label">Платник податку</div>
          <div><strong>{org.full_name || org.name}</strong></div>
        </div>
        <div>
          <div className="label">Код ЄДРПОУ</div>
          <div><strong>{org.edrpou ?? '—'}</strong></div>
        </div>
        <div>
          <div className="label">Індивідуальний податковий номер (ІПН)</div>
          <div>{org.vat_number || org.inn || '—'}</div>
        </div>
        <div>
          <div className="label">Звітний (податковий) період</div>
          <div>{MONTHS_UK[month - 1]} {year}</div>
        </div>
      </div>

      <h2>Розділ I. Податкові зобов'язання</h2>
      <table>
        <thead>
          <tr>
            <th style={{ width: 60 }}>Код рядка</th>
            <th>Найменування показника</th>
            <th style={{ width: 130 }}>Колонка А — обсяг постачання (база), грн</th>
            <th style={{ width: 130 }}>Колонка Б — сума ПДВ, грн</th>
          </tr>
        </thead>
        <tbody>
          {d.obligations.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: 'center', color: '#666' }}>
              Операцій за ставкою ПДВ за період не було.
            </td></tr>
          )}
          {d.obligations.map((r, i) => (
            <tr key={r.rate_code}>
              <td className="line">1.{i + 1}</td>
              <td>{RATE_LABEL[r.rate_code] ?? r.rate_label}</td>
              <td className="num">{fmt(r.base)}</td>
              <td className="num">{fmt(r.vat)}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 600 }}>
            <td className="line">9</td>
            <td>Усього податкових зобов'язань (рядок 9)</td>
            <td className="num">—</td>
            <td className="num">{fmt(d.total_obligations)}</td>
          </tr>
        </tbody>
      </table>

      <h2>Розділ II. Податковий кредит</h2>
      <table>
        <thead>
          <tr>
            <th style={{ width: 60 }}>Код рядка</th>
            <th>Найменування показника</th>
            <th style={{ width: 130 }}>Колонка А — обсяг придбання, грн</th>
            <th style={{ width: 130 }}>Колонка Б — сума ПДВ, грн</th>
          </tr>
        </thead>
        <tbody>
          {d.credit.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: 'center', color: '#666' }}>
              Операцій придбання за період не було.
            </td></tr>
          )}
          {d.credit.map((r, i) => (
            <tr key={r.rate_code}>
              <td className="line">10.{i + 1}</td>
              <td>{RATE_LABEL[r.rate_code] ?? r.rate_label}</td>
              <td className="num">{fmt(r.base)}</td>
              <td className="num">{fmt(r.vat)}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 600 }}>
            <td className="line">17</td>
            <td>Усього податкового кредиту (рядок 17)</td>
            <td className="num">—</td>
            <td className="num">{fmt(d.total_credit)}</td>
          </tr>
        </tbody>
      </table>

      <div className="summary">
        <h2 style={{ marginTop: 0 }}>Розділ III. Розрахунки за звітний період</h2>
        {Number(d.to_pay) > 0 && (
          <p style={{ margin: '0.25rem 0' }}>
            <strong>Рядок 18.</strong> Позитивне значення — сума ПДВ, яка підлягає
            сплаті до бюджету: <strong>{fmt(d.to_pay)} грн</strong>
            <span style={{ color: '#666' }}> (≈ {fmt0(d.to_pay)} грн у декларації)</span>
          </p>
        )}
        {Number(d.to_refund) > 0 && (
          <p style={{ margin: '0.25rem 0' }}>
            <strong>Рядок 19.</strong> Від'ємне значення — сума, що
            переноситься / підлягає бюджетному відшкодуванню:
            <strong> {fmt(d.to_refund)} грн</strong>
            <span style={{ color: '#666' }}> (≈ {fmt0(d.to_refund)} грн у декларації)</span>
          </p>
        )}
        {Number(d.to_pay) === 0 && Number(d.to_refund) === 0 && (
          <p style={{ margin: '0.25rem 0' }}>Підсумок за період: 0,00 грн.</p>
        )}
      </div>

      <div className="signs">
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">Керівник (підпис, П.І.Б.)</div>
        </div>
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">Головний бухгалтер (підпис, П.І.Б.)</div>
        </div>
      </div>

      <div className="draft-warning">
        <strong>⚠ Чорновик для звірки.</strong> Цей документ сформовано локальною
        обліковою системою як інформаційну форму для перевірки сум перед
        поданням. Реальне подання декларації здійснюється через кабінет
        платника податків ДПС з накладенням КЕП на офіційну XML-форму
        Ф0200124. Структура форми (коди рядків, додатки Д1–Д8) підлягає
        звірці з актуальним наказом Міністерства фінансів України.
      </div>
    </div>
  );
}
